<script module lang="ts">
  // For some pages, we want to make sure that the javascript memory context is wiped out before navigating to other pages.
  // This is because the JS we include on those pages weren't designed to be used as a SPA.

  // Examples of this are the checkout, which includes JS from atlas (editor.js, checkout.js).
  const reloadOnNavigateContextKey = Symbol('reload-on-navigate-context');
  type ReloadOnNavigateContext = { value: boolean };
  const createReloadOnNavigateContext = (value: boolean): ReloadOnNavigateContext => {
    const runeReference = $state({ value });
    setContext(reloadOnNavigateContextKey, runeReference);
    return runeReference;
  };
  export const setReloadOnNavigate = (): void => {
    const context = hasContext(reloadOnNavigateContextKey)
      ? getContext<ReloadOnNavigateContext>(reloadOnNavigateContextKey)
      : undefined;
    if (context) {
      context.value = true;
    } else {
      logger().info('ReloadOnNavigateContext-context has not been initialized.', new Error());
    }
  };
</script>

<script lang="ts">
  import { getContext, hasContext, mount, onMount, setContext } from 'svelte';
  import {
    useStoryblokBridge,
    StoryblokComponent,
    storyblokInit,
    type SbSvelteComponentsMap,
  } from '@storyblok/svelte';
  import { Breadcrumbs } from '@stickerapp-org/athena/Breadcrumbs';
  import { LoginForm, type LoginFormHandler } from '@stickerapp-org/athena/LoginForm';
  import type {
    StoryblokStory,
    PageBlock,
    AllBlocks,
    AllBlockNames,
    CheckoutBlock,
  } from '@storyblok-types';

  import { Page, PageSection } from '@stickerapp-org/athena/Page';
  import { Type } from '@sinclair/typebox';
  import { Value } from '@sinclair/typebox/value';
  import Modal from '@athena-lib/Modal/Modal.svelte';
  import { initializeColorContext } from '@athena-lib/container-context.svelte';
  import BlogLayout from '@athena-lib/BlogLayout/BlogLayout.svelte';
  import { setLocalizationContext } from '@athena-lib/i18n';
  import clsx from 'clsx';
  import { setLoginContext } from '@athena-lib/loginContext.svelte';
  import { attempt } from '@athena-lib/attempt';
  import LocalizationBanner from '@athena-lib/LocalizationBanner/LocalizationBanner.svelte';
  import { getAddedCustomSticker } from '@athena-lib/AddedToCartView/AddedToCartView.svelte';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import type { LoginFormTrackingEvent } from '@athena-lib/LoginForm/types';
  import type { Part } from '@athena-lib/SvgPreview/types';
  import LiveLineItemStickerPreview from '@athena-lib/LivePreview/StickerPreview/LiveLineItemStickerPreview.svelte';
  import { STICKER_PREVIEW_CONTEXT_KEY, type StickerPreviewContext } from '@athena-lib/LivePreview';
  import { ProductItem } from '@stickerapp-org/nomisma';
  import type { ProductItem as CheckoutProductItem } from '@stickerapp-org/checkout-sdk';
  import type { CheckoutSdk } from '@athena-lib/Checkout/utils';
  import SimpleHeader from '../../components/SimpleHeader.svelte';
  import Header from '../../components/Header.svelte';
  import Footer from '../../components/Footer.svelte';
  import { isHome } from '../routing';
  import ExternalDataSourceComponent from '../../components/ExternalDataSourceComponent.svelte';
  import type { ExpandDataJson } from '../api/expand-data/+server';
  import WithTranslations from '../../components/WithTranslations.svelte';
  import CheckoutDrawer from '../../components/CheckoutDrawer.svelte';
  import SimpleFooter from '../../components/SimpleFooter.svelte';
  import { ensureNoLeadingSlash } from '../../components/shared/url-utils';
  import GlobalEditor from '../../components/GlobalEditor.svelte';
  import GlobalSdkError from '../../components/GlobalSdkError.svelte';
  import CacheMeta from './CacheMeta.svelte';
  import { initializeImageContext } from './image-context';
  import { fontUrls } from './fonts';
  import { currentOriginalSlug } from './currentOriginalSlug.svelte';
  import { findNeededComponents } from '$lib/find-needed-components';
  import { preventLinkClicksWhenInEditorMode } from '$lib/utilities/storyblok';
  import { getLoadedComponentsMap } from '$lib/componentsMap';
  import { setStickerShopContext } from '$lib/utilities/stickerShopContext';
  import { browser } from '$app/environment';
  import { afterNavigate, onNavigate } from '$app/navigation';
  import logger from '$lib/logger';
  import { stripPagesSlugPathname } from '$lib/utilities/stripPagesSlug';
  import { pushVariable } from '$lib/gtm/setup';
  import { GtmEventNames, GtmVariableNames, type KlaviyoFormEvent } from '$lib/gtm/types';
  import { trackCTAButton, trackKlaviyoForm, trackForm } from '$lib/gtm/event-trigger';
  import { post } from '$lib/http';
  import { page as pageState } from '$app/state';
  import {
    getCheckoutSdk,
    getProductsTranslators,
    getSuggestedPostalCode,
    initializeCheckoutSdk,
    setCheckoutState,
  } from '$lib/checkout.svelte';
  import {
    checkoutDrawerActions,
    checkoutDrawerContext,
  } from '$lib/utilities/checkoutDrawerContext.svelte';

  let { data } = $props();

  setContext<StickerPreviewContext>(STICKER_PREVIEW_CONTEXT_KEY, data.stickerPreviewContext);

  const translations = getCommonTranslations();

  if (data.market.useNewCheckout && browser && !getCheckoutSdk()) {
    const siteUrl = 'https://' + data.market.domain;
    const checkoutParameters = {
      checkoutApiBaseUrl: data.checkoutApiBaseUrl,
      jwt: data.jwt,
      userCountryCode: data.userCountryCode,
      locale: data.locale,
      siteUrl,
      psp: data.market.psp,
      checkoutPageUrl: data.checkoutUrl,
      orderConfirmationPageUrl: data.orderConfirmationUrl,
      atlasSessionId: data.atlasSessionId,
      translations,
    };
    const { cartInstance, checkoutContextKey } = initializeCheckoutSdk(checkoutParameters);
    setCheckoutState(checkoutContextKey, {
      sdk: cartInstance,
      settings: data.globalBlocks.checkoutSettings,
      suggestedPostalCode: data.suggestedPostalCode ?? undefined,
    });
  }

  const sdk = $derived(getCheckoutSdk());
  const atlasTranslators = $derived(getProductsTranslators());

  if (browser) {
    window.updateCustomSticker = (...args: Parameters<CheckoutSdk['updateCustomSticker']>) => {
      if (!sdk) {
        throw new Error('No sdk initialized');
      }

      return sdk.updateCustomSticker(...args);
    };
    window.addCustomSticker = (...args: Parameters<CheckoutSdk['createCustomSticker']>) => {
      if (!sdk) {
        throw new Error('No sdk initialized');
      }

      return sdk.createCustomSticker(...args).then((result) => {
        sdk.actions.addCustomStickerLineItem(result.id, result.attributes.quantity ?? 1);
        const addedCustomSticker = getAddedCustomSticker(
          result,
          atlasTranslators?.translateProductName ?? ((k) => k),
          translations.customStickerType,
          data.locale,
        );

        checkoutDrawerActions.addItemToCart(addedCustomSticker);
      });
    };
    window.addLinesFromOrder = (
      ...args: Parameters<CheckoutSdk['actions']['addLinesFromOrder']>
    ) => {
      if (!sdk) {
        throw new Error('No sdk initialized');
      }
      return sdk.actions.addLinesFromOrder(...args);
    };
  }
  $effect(() => {
    if (
      data.pageData.type === 'internal' &&
      data.pageData.story?.content.component === 'checkout'
    ) {
      document.getElementsByTagName('main')[0]?.classList.add('bg-st-gray-100');
      return () => {
        document.getElementsByTagName('main')[0]?.classList.remove('bg-st-gray-100');
      };
    }
  });

  initializeColorContext({ sm: 'light', md: 'light', lg: 'light' });
  setStickerShopContext(data.stickerShopMeta);

  setLoginContext({
    isLoggedIn: data.isLoggedIn,
    userId: data.userId,
  });
  let pageData = $state(data.pageData);
  let language = $derived(data.locale.language);

  const url = $derived(new URL(data.url));

  const reloadOnNavigateContext = createReloadOnNavigateContext(false);
  const reloadOnNavigate = $derived.by(() => {
    return reloadOnNavigateContext.value;
  });

  $effect(() => {
    if (!reloadOnNavigate) {
      return;
    }
    onNavigate((url) => {
      if (!url.to) {
        return;
      }
      window.location.href = url.to.url.toString();
    });
  });

  const closeLoginModal = () => {
    history.back();
  };

  const getModalState = () => {
    const hash = browser ? window.location.hash : url.hash;
    return hash === '#modal-login';
  };
  const onHashChange = () => {
    loginModalIsVisible = getModalState();
  };

  // Global click listener to track cta buttons
  const onButtonClick = (event: MouseEvent) => {
    const element = event.target as HTMLElement;
    if (!element) {
      return;
    }
    const allowedElements = ['a'];
    const elementName = element.nodeName.toLowerCase();
    if (allowedElements.indexOf(elementName) < 0) {
      return;
    }
    let itemText = element.innerText ?? '';
    let itemUrl = element.getAttribute('href') ?? '';
    let eventName: GtmEventNames | undefined;

    if (element.matches('.btn-primary')) {
      eventName = GtmEventNames.CTAPrimary;
    }

    if (element.matches('.btn-secondary')) {
      eventName = GtmEventNames.CTASecondary;
    }

    if (element.matches('.btn-tertiary')) {
      eventName = GtmEventNames.CTATertiary;
    }

    if (!eventName) {
      return;
    }

    trackCTAButton({
      event: eventName,
      event_params: {
        event_action: 'cta',
        label: 'button_click',
        item_url: window.location.hostname + itemUrl,
        item_text: itemText,
      },
    });
  };

  let loginModalIsVisible = $state(getModalState());
  $effect(() => {
    // this effect listens to navigations outside of svelte, e.g. atlas js
    window.addEventListener('hashchange', onHashChange);
    window.addEventListener('click', (e) => onButtonClick(e));
    window.addEventListener('klaviyoForms', (e) =>
      onTrackKlaviyo(e as CustomEvent<KlaviyoFormEvent>),
    );
    return () => {
      window.removeEventListener('hashchange', onHashChange);
      window.removeEventListener('click', (e) => onButtonClick(e));
      window.removeEventListener('klaviyoForms', (e) =>
        onTrackKlaviyo(e as CustomEvent<KlaviyoFormEvent>),
      );
    };
  });
  // this effect listens to internal navigations svelte, e.g. atlas js
  afterNavigate(onHashChange);

  let page = $derived(
    pageData.type === 'internal' ? (pageData.story?.content as Partial<PageBlock>) : null,
  );

  initializeImageContext();

  // We assume here that all pages has a property called `metatags` rather than assume that
  // `data.story.content` is always a PageBlock
  const metatags = $derived(page?.metatags);
  const customStructuredData = $derived(page?.structuredData);
  const expanderStructuredData = $derived(data?.expanderStructuredData);

  $effect(() => {
    pageData = data.pageData;
  });

  setLocalizationContext(data.locale);

  let latestUpdateIndex = $state(0);

  const story = $derived.by(() => {
    if (pageData.type === 'internal' && pageData.story) {
      return pageData.story;
    }
    return null;
  });
  $effect(() => {
    const storySlug =
      stripPagesSlugPathname(story?.default_full_slug ?? '')?.strippedUrl ??
      story?.default_full_slug;
    currentOriginalSlug.slug = ensureNoLeadingSlash(storySlug ?? window.location.pathname);
  });

  onMount(async () => {
    if (data.userId) {
      pushVariable({ [GtmVariableNames.UserId]: data.userId });
    }

    if (!story) {
      return;
    }

    // Load live preview for receipt page
    if (story.content.component === 'customer') {
      const checkoutItemElements = document.querySelectorAll('.lineItemLivePreviewContainer');
      const items = Array.from(checkoutItemElements) as HTMLElement[];
      const receiptPageId = Math.random().toString(36).substring(2, 15);
      /* Mounting live previews. */
      items.forEach((element, id) => {
        // Safely parse JSON with proper type assertions
        let productData: ProductItem | null = null;
        let parts: Part[] | null = null;

        try {
          if (element.dataset.productItem) {
            productData = JSON.parse(element.dataset.productItem) as ProductItem;
          }

          if (element.dataset.parts) {
            parts = JSON.parse(element.dataset.parts) as Part[];
          }
          // Mount component if data is available
          if (productData && parts) {
            const realProductItem = new ProductItem(
              productData.getProductFamilyName(),
              productData.getProductName(),
              productData.getAttributes(),
            );
            // realProductItem.fromJSON(productData);
            mount(LiveLineItemStickerPreview, {
              target: element,
              props: {
                customStickerPromise: Promise.resolve({
                  id: `${receiptPageId}-${id}`,
                  version: 'dummyVersion',
                  parts,
                  productItem: realProductItem as unknown as CheckoutProductItem,
                  attributes: realProductItem.getAttributes(),
                }),
              },
            });
          }
        } catch (error) {
          console.error('Error parsing data attributes:', error);
        }
      });
    }

    preventLinkClicksWhenInEditorMode();
    useStoryblokBridge(story.id, (newStory) => {
      const expandDataJson: ExpandDataJson = {
        ISbStoryData: newStory,
        originalUrl: window.location.href,
      };
      latestUpdateIndex += 1;
      const currentUpdateIndex = latestUpdateIndex;

      fetch(`/api/expand-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(expandDataJson),
      })
        .then((x) => x.json())
        .then((expandedStory) => {
          if (latestUpdateIndex !== currentUpdateIndex) {
            return;
          }
          return getLoadedComponentsMap(findNeededComponents(expandedStory)).then((components) => ({
            components,
            expandedStory,
          }));
        })
        .then((result) => {
          if (!result) {
            return;
          }
          const { components, expandedStory } = result;
          storyblokInit({
            components: components as SbSvelteComponentsMap,
          });
          pageData = {
            type: 'internal',
            story: expandedStory as StoryblokStory<AllBlocks>,
          };
        });
    });

    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener('click', function (e) {
        const element = attempt(() =>
          document.querySelector(anchor.getAttribute('href') ?? ''),
        )?.value;
        if (!element) {
          return;
        }
        e.preventDefault();

        element.scrollIntoView({
          behavior: 'smooth',
        });
      });
    });

    const params = new URLSearchParams(window.location.search);
    const code = params.getAll('discount-code');
    if (code.length > 0) {
      checkoutSdk?.actions.addCode(code[0]);
    }
  });

  const title = $derived.by(() => {
    if (metatags?.title) {
      return metatags.title;
    }
    if (pageData.type === 'internal' && pageData.story) {
      return pageData.story.name;
    }
    if (pageData.type === 'external') {
      return pageData.data.name;
    }
    return '';
  });

  const typefaces: {
    [key: string]: string;
  } = {
    jp: 'noto-sans-jp',
    default: 'noto-sans',
  };

  const typefaceName: string = $derived(typefaces[language] || typefaces.default);

  const LoginSchema = Type.Object({
    notifications: Type.Array(Type.String()),
    success: Type.Boolean(),
  });

  const handleLoginForm = (endpoint: string, onSuccess?: () => void): LoginFormHandler => {
    return (e) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);

      const data = new URLSearchParams();
      for (const pair of formData) {
        data.append(pair[0], pair[1] as string);
      }
      return fetch(endpoint, {
        method: 'POST',
        body: data,
        headers: { Accept: 'application/json' },
      })
        .then((x) => x.json())
        .then((json) => Value.Decode(LoginSchema, json))
        .then((x) => {
          if (x.success && onSuccess) {
            onSuccess();
          }
          return x;
        })
        .catch(() => {
          return { success: false, notifications: ['An error occurred'] };
        });
    };
  };

  const onSubmitResetPassword: LoginFormHandler = handleLoginForm(
    '/customer/ajax_request_password_reset',
  );

  const onSubmitSignUp: LoginFormHandler = handleLoginForm('/customer/ajax_sign_up');

  const onSubmitLogin: LoginFormHandler = handleLoginForm('/customer/ajax_login', () => {
    const noRedirectToCustomer =
      checkoutDrawerContext.value?.type === 'Login'
        ? checkoutDrawerContext.value.noRedirectToCustomer
        : false;

    if (!noRedirectToCustomer) {
      // don't use goto here, as we want to reload the page
      location.assign('/customer');
    } else {
      setLoginContext({ isLoggedIn: true });
      if (data.pageData.type === 'internal' && data.pageData.story) {
        if (data.pageData.story?.content.component === 'checkout') {
          location.reload();
        }
      }
      checkoutDrawerActions.close();
    }
  });
  const showBlogHeader = $derived.by(() => {
    const blogPages: Array<AllBlockNames> = ['blogCategory', 'author', 'blogLanding'];

    return (
      data.pageData.type === 'internal' &&
      data.pageData.story?.content.component &&
      blogPages.includes(data.pageData.story?.content.component)
    );
  });

  const fontClass = $derived.by(() => {
    switch (language) {
      case 'jp':
        return 'font-noto-sans-jp';
      default:
        return 'font-noto-sans';
    }
  });

  const fontName = $derived.by(() => {
    switch (language) {
      case 'jp':
        return 'Noto Sans JP';
      default:
        return 'Noto Sans';
    }
  });

  const fontUrl = (language: string, weight: 400 | 900, format: 'woff' | 'woff2') => {
    const fontLanguage = language === 'jp' ? 'jp' : 'default';
    return fontUrls[fontLanguage][weight][format];
  };

  const isHomePage = $derived(story ? isHome(story) : false);
  let locationBannerDismissed = $state(false);

  const dismissLocationBanner = async () => {
    locationBannerDismissed = true;
    await post(
      '/api/info-banner',
      {
        action: 'dismiss',
        id: 'localizationBanner',
      },
      Type.Unknown(),
    );
  };

  const navigateToMarket = () => {
    if (!data.localizationBannerData) {
      return;
    }

    const { targetUrl } = data.localizationBannerData;

    window.location.href = targetUrl;
  };

  const checkoutSdk = $derived(getCheckoutSdk());
  const showSimpleLayout = $derived.by(() => {
    const p = page as Partial<CheckoutBlock> | null;
    if (!checkoutSdk) {
      return false;
    }
    if (data.market.isManualOrder) {
      return true;
    }
    return p?.component === 'checkout' && !pageState.url.searchParams.has('preview');
  });

  const postalCode = $derived.by(getSuggestedPostalCode);

  function onLoginFormTracking(event: LoginFormTrackingEvent) {
    trackForm({
      event: GtmEventNames.FormInteraction,
      event_params: event,
    });
  }

  function onTrackKlaviyo(event: CustomEvent<KlaviyoFormEvent>) {
    if (localStorage.getItem(event.detail.formId) === 'submitted') {
      return;
    }
    switch (event.detail.type) {
      case 'open':
        trackKlaviyoForm('klaviyo_form_popup_show', event.detail.formId, story?.content.component);
        break;
      case 'embedOpen':
        trackKlaviyoForm('klaviyo_form_embed_show', event.detail.formId, story?.content.component);
        break;
      case 'close':
        trackKlaviyoForm('klaviyo_form_popup_close', event.detail.formId, story?.content.component);
        break;
      case 'stepSubmit':
        if (story?.content.component === 'footer') {
          localStorage.setItem(event.detail.formId, 'submitted');
        }
        trackKlaviyoForm('klaviyo_form_submit', event.detail.formId, story?.content.component);
        break;
    }
  }
</script>

<svelte:head>
  <title>{title}</title>
  {#if metatags?.description}
    <meta name="description" content={metatags?.description} />
  {/if}

  {#if metatags?.og_title}
    <meta name="og:title" content={metatags?.og_title} />
  {/if}
  {#if metatags?.og_description}
    <meta name="og:description" content={metatags?.og_description} />
  {/if}
  {#if metatags?.og_image}
    <meta name="og:image" content={metatags?.og_image} />
  {/if}

  {#if data.hreflangs}
    {#each data.hreflangs as hreflang}
      <link rel="alternate" hreflang={hreflang.language} href={hreflang.url} />
    {/each}
  {/if}

  {#if data.canonicalUrl}
    <link rel="canonical" href={data.canonicalUrl} />
  {/if}

  {#if data.noindex}
    <meta name="robots" content="noindex" />
  {/if}

  {#if customStructuredData}
    <!-- eslint-disable-next-line svelte/no-at-html-tags -->
    {@html `<scri${''}pt type="application/ld+json">${customStructuredData}</scri${''}pt>`}
  {/if}
  {#if expanderStructuredData}
    <!-- eslint-disable-next-line svelte/no-at-html-tags -->
    {@html `<scri${''}pt type="application/ld+json">${expanderStructuredData}</scri${''}pt>`}
  {/if}

  {#if new URL(data.url).searchParams.get('customfont') !== 'off'}
    <link
      rel="preload"
      href={fontUrl(language, 400, 'woff2')}
      as="font"
      type="font/woff2"
      crossorigin="anonymous"
    />
    <link
      rel="preload"
      href={fontUrl(language, 900, 'woff2')}
      as="font"
      type="font/woff2"
      crossorigin="anonymous"
    />

    <!-- eslint-disable-next-line svelte/no-at-html-tags -->
    {@html `
      <style>
        @font-face {
          font-display: swap;
          font-family: '${fontName}';
          font-style: normal;
          font-weight: 400;
          src:
            url('${fontUrl(typefaceName, 400, 'woff2')}') format('woff2'),
            url('${fontUrl(typefaceName, 400, 'woff')}') format('woff');
        }

        @font-face {
          font-display: swap;
          font-family: '${fontName}';
          font-style: normal;
          font-weight: 900;
          src:
            url('${fontUrl(typefaceName, 900, 'woff2')}') format('woff2'),
            url('${fontUrl(typefaceName, 900, 'woff')}') format('woff');
        }
      </style>
  `}
  {/if}

  <!-- eslint-disable-next-line svelte/no-at-html-tags -->
  {@html data.headHtml}

  {#if data.headScript}
    <!-- eslint-disable-next-line svelte/no-at-html-tags -->
    {@html `<scri${''}pt type="text/javascript" nonce="${data.nonce}">${data.headScript}</scri${''}pt>`}
  {/if}
</svelte:head>

<GlobalSdkError {...data.globalBlocks.checkoutSettings} />
{#if checkoutSdk}
  <CheckoutDrawer
    {checkoutSdk}
    {onSubmitLogin}
    {onSubmitResetPassword}
    {onSubmitSignUp}
    onTracking={onLoginFormTracking}
  />
{/if}

{#if data.atlasResources}
  <GlobalEditor atlasResources={data.atlasResources} useNewCheckout={data.market.useNewCheckout} />
{/if}

{#snippet SubHeading()}
  {#if showBlogHeader}
    <BlogLayout
      breadcrumbs={data.breadcrumbs}
      logo={data.blogMeta.logo}
      categories={data.blogMeta.categories}
      blogLandingSlug={data.blogMeta.blogLandingSlug}
    ></BlogLayout>
  {:else if data.breadcrumbs}
    <PageSection width="large" backgroundColor="medium" ySpace="small">
      <Breadcrumbs breadcrumbs={data.breadcrumbs} />
    </PageSection>
  {/if}
{/snippet}

{#if data.cacheMeta}
  <CacheMeta cacheMeta={data.cacheMeta} />
{/if}
{#if story?.content.component === 'layout'}
  <StoryblokComponent blok={story.content} />
{:else}
  <Page {reloadOnNavigate} class={fontClass}>
    {#if !data.market.useNewCheckout && loginModalIsVisible}
      <Modal
        size="sm"
        onClose={() => {
          closeLoginModal();
        }}
        showModal={loginModalIsVisible}
        closable={true}
        height="h-5/6"
      >
        <LoginForm
          onLoginSubmit={onSubmitLogin}
          onResetPasswordSubmit={onSubmitResetPassword}
          onSignUpSubmit={onSubmitSignUp}
          googleLoginUrl="/customer/google_login"
          onTracking={onLoginFormTracking}
        />
      </Modal>
    {/if}
    <header class={clsx(isHomePage ? 'fixed' : 'sticky', 'top-0 z-10 w-full')}>
      {#if showSimpleLayout}
        <SimpleHeader hideBackButton={data.market.isManualOrder} blok={data.header} />
      {:else}
        <Header
          blok={data.header}
          transparentAtTop={isHomePage}
          useNewCheckout={data.market.useNewCheckout}
          shippingDetails={postalCode}
        />
      {/if}
    </header>
    <main class="flex grow flex-col">
      {@render SubHeading()}
      {#if story}
        {#key story.full_slug}
          <StoryblokComponent blok={story.content} slug={data.determinedSlug} {isHomePage} />
        {/key}
      {/if}
      {#if pageData.type === 'external'}
        {#key pageData.data.slug}
          <ExternalDataSourceComponent data={pageData.data} />
        {/key}
      {/if}
      {#if data.localizationBannerData && !locationBannerDismissed && !data.market.useKameleoon}
        <WithTranslations translations={data.localizationBannerData.translations}>
          <div
            id="localization-banner"
            class="fixed bottom-0 left-0 right-0 z-20 animate-enter-bottom md:bottom-auto md:left-auto md:right-5 md:top-28 md:animate-enter-right"
          >
            <LocalizationBanner
              countryImageSource={data.localizationBannerData.flagUrl}
              onClose={dismissLocationBanner}
              onContinue={navigateToMarket}
              linkOptions={{ href: data.localizationBannerData.targetUrl }}
            />
          </div>
        </WithTranslations>
      {/if}
    </main>
    {#snippet footer()}
      {#if !data.market.isManualOrder}
        <footer>
          {#if showSimpleLayout}
            <SimpleFooter blok={{ ...data.footer, markets: data.markets }} />
          {:else}
            <Footer
              blok={{
                ...data.footer,
                markets: data.markets,
              }}
            />
          {/if}
        </footer>
      {/if}
    {/snippet}
  </Page>
{/if}
